import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Collapse,
  useTheme,
  Box,
  useMediaQuery,
  Popover,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Chip,
} from "@mui/material";
import {
  IconHome,
  IconUser,
  IconUserCircle,
  IconSettings,
  IconSchool,
  IconBooks,
  IconBus,
  IconTools,
  IconReport,
  IconSend,
  IconUsers,
  IconClock,
  IconTable,
  IconCash,
  IconChalkboard,
  IconMinus,
  IconPlus,
  IconCashBanknote,
  IconMedal,
  IconCopy,
  IconVideo,
  IconSlice,
  IconCreditCard,
  IconWallet,
  IconBriefcase,
  IconMessage,
  IconMenuDeep,
  IconTrash,
  IconClockHour8,
  IconCircles,
  IconDownload,
  IconEdit,
  IconClipboardText,
  IconEye,
  IconEyeOff,
  IconApps,
  IconBrandWhatsapp,
  IconBrandInstagram,
  IconBrandFacebook,
  IconBrandX,
  IconUserCog,
  IconWorldUpload,
  IconBell,
  IconCalendarDue,
  IconPlaylistAdd,
} from "@tabler/icons-react";
import "./Sidebar.scss";
import CustomeSearchInput from "../FormFields/CustomeSearchInput";
import { useAuth } from "../../hooks/useAuth";
import { GetAllSideBarUpdatedMenu } from "../../api/auth/authService";
import { Roles } from "../../constants/params/constants";
import SidebarStatusTag from "./SidebarStatusTag";
import { IconWifi } from "@tabler/icons-react";

const normalizeColor = (val) => (String(val || "").replace(/\s|!important/gi, "").toLowerCase());

const getMenuIcon = (iconName) => {
  const iconMap = {
    dashboard: IconHome,
    people: IconUser,
    person: IconUser,
    Person: IconUser,
    staff: IconUsers,
    account: IconUsers,
    Account: IconUsers,
    users: IconUsers,
    Users: IconUsers,
    classsection: IconChalkboard,
    class: IconChalkboard,
    Class: IconChalkboard,
    fees: IconCash,
    attendance: IconClockHour8,
    timetable: IconTable,
    communication: IconSend,
    roles: IconCircles,
    setting: IconSettings,
    download: IconDownload,
    edit: IconEdit,
    clipboardtext: IconClipboardText,
    eye: IconEye,
    eyeoff: IconEyeOff,
    apps: IconApps,
    whatsapp: IconBrandWhatsapp,
    instagram: IconBrandInstagram,
    facebook: IconBrandFacebook,
    x: IconBrandX,
    usercog: IconUserCog,
    worldupload: IconWorldUpload,
    playlistadd: IconPlaylistAdd,
    calendardue: IconCalendarDue,
    bell: IconBell,
    trash: IconTrash,
    menudeep: IconMenuDeep,
    school: IconSchool,
    message: IconMessage,
    briefcase: IconBriefcase,
    wallet: IconWallet,
    cashbanknote: IconCashBanknote,
    creditcard: IconCreditCard,
    slice: IconSlice,
    video: IconVideo,
    copy: IconCopy,
    medal: IconMedal,
    Dashboard: IconHome,
    People: IconUser,
    Staff: IconUsers,
    ClassSection: IconChalkboard,
    Fees: IconCash,
    Attendance: IconClockHour8,
    Timetable: IconTable,
    Communication: IconSend,
    Roles: IconCircles,
    Setting: IconSettings,
    Download: IconDownload,
    Edit: IconEdit,
    IconClipboardText: IconClipboardText,
    IconEye: IconEye,
    IconEyeOff: IconEyeOff,
    IconApps: IconApps,
    IconBrandWhatsapp: IconBrandWhatsapp,
    IconBrandInstagram: IconBrandInstagram,
    IconBrandFacebook: IconBrandFacebook,
    IconBrandX: IconBrandX,
    IconUserCog: IconUserCog,
    IconWorldUpload: IconWorldUpload,
    IconPlaylistAdd: IconPlaylistAdd,
    IconCalendarDue: IconCalendarDue,
    IconBell: IconBell,
    IconTrash: IconTrash,
    IconMenuDeep: IconMenuDeep,
    IconSchool: IconSchool,
    IconMessage: IconMessage,
    IconBriefcase: IconBriefcase,
    IconWallet: IconWallet,
    IconCashBanknote: IconCashBanknote,
    IconCreditCard: IconCreditCard,
    IconSlice: IconSlice,
    IconVideo: IconVideo,
    IconCopy: IconCopy,
    IconMedal: IconMedal,
  }
  return iconMap[iconName] || IconSettings
}

const Sidebar = ({ open = true, toggleSidebar }) => {
  const theme = useTheme();
  const customColors = useSelector((state) => state.theme.customColors);
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenus, setOpenMenus] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const menuState = useSelector((state) => state.menu);
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeFloatingMenu, setActiveFloatingMenu] = useState(null);
  const isDarkMode = useSelector((state) => state.theme.isDarkMode);
  const isDark = theme.palette.mode === 'dark';

  const sidebarColorRaw = customColors && customColors.sidebar || theme.palette.background.sidebarbg;
  const sidebarColor = normalizeColor(sidebarColorRaw);

  const isSidebarDark = ['#000', '#000000', '#180c0d', '#111417', '#134686', '#162891', '#121b2e', '#1d4561ff'].includes(sidebarColor);
  const isSidebarWhite = ['#fff', '#ffffff', '#fff!important', '#ffffff!important'].includes(sidebarColor);

  const connectorColor = isDarkMode
    ? theme.palette.custom.borderColor
    : (isSidebarDark ? '#4d688a' : (isSidebarWhite ? '#e9edf4' : theme.palette.custom.borderColor));
  const [sidebarRoutes, setSidebarRoutes] = useState([]);

  const [networkSpeed, setNetworkSpeed] = useState({ mbps: null, byteSpeedText: null, color: undefined });
  const [isNetworkSupported, setIsNetworkSupported] = useState(true);

  const getContrastColor = (hexColor) => {
    if (!hexColor) return "#FFFFFF";

    const c = hexColor.substring(1);
    const rgb = parseInt(c, 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = rgb & 0xff;

    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    return brightness > 150 ? "#000000" : "#FFFFFF";
  };

  const getNormalMenuTextColor = () => {
    if (customColors?.sidebar) {
      return getContrastColor(sidebarColor);
    }

    return isSidebarDark ? theme.palette.custom.whitetext : theme.palette.custom.menuText;
  };

  const getActiveMenuTextColor = () => {
    if (customColors?.activeTextColor) {
      return customColors.activeTextColor;
    }

    return theme.palette.custom.whitetext;
  };

  const normalMenuTextColor = getNormalMenuTextColor();
  const activeMenuTextColor = getActiveMenuTextColor();
  const sidebarIconColor = customColors?.sidebar ? normalMenuTextColor : undefined;

  // const sidebarTextColor = getSidebarTextColor();
  // const activeMenuTextColor = getActiveMenuTextColor();
  // const sidebarIconColor = customColors?.sidebar ? sidebarTextColor : undefined;

  const mergeSidebarLabels = (routes, apiData) => {
    if (!routes || !apiData) return routes;

    return routes.map((menu) => {
      const updatedParent = apiData.find(apiItem => apiItem.menuId === menu.subMenuId);
      const parentName = updatedParent?.newLabel || menu.subMenuName;
      const parentMenuIcon = updatedParent?.icon || menu.menuIcon;
      return {
        ...menu,
        subMenuName: parentName,
        menuIcon: parentMenuIcon,
        children: menu.children?.map((child) => {
          const updatedChild = apiData.find(apiItem => apiItem.menuId === child.subMenuId);
          return {
            ...child,
            subMenuName: updatedChild?.newLabel || child.subMenuName,
          };
        }) || [],
      };
    });
  };

  const loadMenu = async () => {
    if (!user?.schoolID) {
      return;
    }
    const formData = new FormData();
    formData.append("LabelID", "");
    formData.append("SchoolID", user?.schoolID);

    const apiResponse = await GetAllSideBarUpdatedMenu(user?.schoolID, formData);
    const mergedRoutes = mergeSidebarLabels(user.routes, apiResponse?.data || []);
    setSidebarRoutes(mergedRoutes);
  };

  const [childrenList, setChildrenList] = useState([]);
  const [selectedChild, setSelectedChild] = useState("");

  useEffect(() => {
    setChildrenList([
      { id: 1, name: "Aarav Sharma" },
      { id: 2, name: "Kiara Sharma" },
    ]);
  }, [user]);

  useEffect(() => {
    loadMenu();
  }, [user?.routes, user?.schoolID, menuState.lastUpdated]);

  
  // const renderSubMenu = (items) => (
  //   <Box
  //     className="sub-menu"
  //     sx={{
  //       position: "relative",
  //       "&::before": { background: connectorColor },
  //     }}
  //   >
  //     {items.filter((subm) => subm.isMenuShow === true).map((item) => {
  //       const isActive = location.pathname === item.routePath;
  //        const subMenuActiveColor = customColors?.activeTextColor || theme.palette.custom.colorActive
  //       return (
  //         <Box
  //           key={item.subMenuId}
  //           className="sub-menu-item"
  //           sx={{
  //             position: "relative",
  //             "&::after": {
  //               borderBottom: `2px solid ${connectorColor}`,
  //               borderLeft: `2px solid ${connectorColor}`,
  //             },
  //           }}
  //         >
  //           <ListItemButton
  //             className="sub-menu-button"
  //             onClick={() => {
  //               navigate(item.routePath);
  //               if (isMobile) toggleSidebar();
  //             }}
  //             sx={{
  //               color: isActive
  //                 ? subMenuActiveColor
  //                 : normalMenuTextColor,
  //               borderRadius: 1,
  //               "&:hover": {
  //                 color: subMenuActiveColor,
  //               },
  //             }}
  //           >
  //             <Box className="sub-menu-content" display="flex" alignItems="center" gap={1}>
  //               {isActive && (
  //                 <Box
  //                   className="active-dot"
  //                   sx={{
  //                     width: 8,
  //                     height: 8,
  //                     borderRadius: "50%",
  //                     backgroundColor: subMenuActiveColor,
  //                   }}
  //                 />
  //               )}
  //               <Box component="span" className="sub-menu-text" style={{ color: 'inherit',whiteSpace:'nowrap' }}>
  //                 {item.subMenuName}
  //                 {item.tag && <SidebarStatusTag type={item.tag} />}
  //               </Box>
  //             </Box>
  //           </ListItemButton>
  //         </Box>
  //       );
  //     })}
  //   </Box>
  // );
  //  const handleMenu = (section) => {
  //   if (isMobile && !open) {
  //     toggleSidebar();
  //     return;
  //   }

  //   if (section?.children?.length === 0) {
  //     navigate(section.routePath);
  //     if (isMobile) toggleSidebar();
  //   } else {
  //     setOpenMenus((prev) => {
  //       const newOpenMenus = {};
  //       newOpenMenus[section.subMenuId] = !prev[section.subMenuId];
  //       return newOpenMenus;
  //     });
  //   }
  // };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
  };

  useEffect(() => {
    if (!sidebarRoutes || sidebarRoutes.length === 0) return;
    
    if (!searchTerm || searchTerm.trim() === '') {
      if (user?.routes) {
        const activeParent = user.routes.find((section) =>
          section?.children?.some(
            (child) => child.routePath === location.pathname
          )
        );
        if (activeParent) {
          setOpenMenus({ [activeParent.subMenuId]: true });
        } else {
          setOpenMenus({});
        }
      }
      return;
    }

    const newOpenMenus = {};
    const searchLower = searchTerm.toLowerCase();

    sidebarRoutes.forEach((menu) => {
      const parentMatch = menu.subMenuName
        ?.toLowerCase()
        .includes(searchLower);

      const childMatch = menu.children?.some((child) =>
        child.subMenuName?.toLowerCase().includes(searchLower)
      );

      if (parentMatch || childMatch) {
        newOpenMenus[menu.subMenuId] = true;
      }
    });

    setOpenMenus(newOpenMenus);
  }, [searchTerm, sidebarRoutes, user?.routes, location.pathname]);

  const renderSubMenu = (items) => (
    <Box
      className="sub-menu"
      sx={{
        position: "relative",
        "&::before": { background: connectorColor },
      }}
    >
      {items.filter((subm) => subm.isMenuShow === true).map((item) => {
        const isActive = location.pathname === item.routePath;
        const subMenuActiveColor = customColors?.activeTextColor || theme.palette.custom.colorActive;
        return (
          <Box
            key={item.subMenuId}
            className="sub-menu-item"
            sx={{
              position: "relative",
              "&::after": {
                borderBottom: `2px solid ${connectorColor}`,
                borderLeft: `2px solid ${connectorColor}`,
              },
            }}
          >
            <ListItemButton
              className="sub-menu-button"
              onClick={() => {
                navigate(item.routePath);
                if (isMobile) toggleSidebar();
              }}
              sx={{
                color: isActive
                  ? subMenuActiveColor
                  : normalMenuTextColor,
                borderRadius: 1,
                fontSize: '12px', // Set font size to 12px
                "&:hover": {
                  color: subMenuActiveColor,
                },
              }}
            >
              <Box className="sub-menu-content" display="flex" alignItems="center" gap={1}>
                {isActive && (
                  <Box
                    className="active-dot"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: subMenuActiveColor,
                    }}
                  />
                )}
                <Box 
                  component="span" 
                  className="sub-menu-text" 
                  style={{ 
                    color: 'inherit',
                    whiteSpace:'nowrap',
                    fontSize: '12px' 
                  }}
                >
                  {item.subMenuName}
                  {item.tag && <SidebarStatusTag type={item.tag} />}
                </Box>
              </Box>
            </ListItemButton>
          </Box>
        );
      })}
    </Box>
  );

  const handleMenu = (section, event) => {
    const hasChildren = section?.children?.length > 0;

    if (!hasChildren) {
      navigate(section.routePath);
      if (isMobile) toggleSidebar();
      return;
    }

    if (!open && !isMobile) {
      setAnchorEl(event.currentTarget);
      setActiveFloatingMenu(section);
    } else {
      setOpenMenus((prev) => ({
        // ...prev,
        [section.subMenuId]: !prev[section.subMenuId],
      }));
    }
  };

  useEffect(() => {
    if (!user?.routes) return;

    const activeParent = user.routes.find((section) =>
      section?.children?.some((child) => child.routePath === location.pathname)
    );

    if (activeParent) {
      setOpenMenus((prev) => ({ ...prev, [activeParent.subMenuId]: true }));
    }
  }, [location.pathname, user?.routes]);

  useEffect(() => {
    setAnchorEl(null);
    setActiveFloatingMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!open && !isMobile) {
      setOpenMenus({});
    } else if (open && !isMobile) {
      if (user?.routes) {
        const activeParent = user.routes.find((section) =>
          section?.children?.some(
            (child) => child.routePath === location.pathname
          )
        );
        if (activeParent) {
          setOpenMenus({ [activeParent.subMenuId]: true });
        }
      }
    }
  }, [open, isMobile, location.pathname, user?.routes]);

  const getFilteredRoutes = () => {
    if (!sidebarRoutes || sidebarRoutes.length === 0) return [];
    
    if (!searchTerm || searchTerm.trim() === '') {
      return sidebarRoutes
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .filter(menu => menu?.isMenuShow === true);
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = [];

    sidebarRoutes.forEach((menu) => {
      if (menu?.isMenuShow !== true) return;

      const parentMatch = menu.subMenuName?.toLowerCase().includes(searchLower);
      
      const matchingChildren = menu.children
        ?.filter(child => 
          child.isMenuShow === true && 
          child.subMenuName?.toLowerCase().includes(searchLower)
        )
        .sort((a, b) => a.sortOrder - b.sortOrder) || [];

      if (parentMatch || matchingChildren.length > 0) {
        filtered.push({
          ...menu,
          children: parentMatch 
            ? menu.children?.filter(child => child.isMenuShow === true).sort((a, b) => a.sortOrder - b.sortOrder) 
            : matchingChildren
        });
      }
    });

    return filtered.sort((a, b) => a.sortOrder - b.sortOrder);
  };

  const filteredRoutes = getFilteredRoutes();

  // const measureLiveSpeed = useCallback(async () => {
  //   if (!navigator.onLine) {
  //     setNetworkSpeed({ mbps: 0, byteSpeedText: "0 KB/s", kbps: 0 });
  //     return;
  //   }

  //   try {
  //     const fileUrl = "https://www.mountsaffronit.com/images/others/ping.png";
  //     const controller = new AbortController();
  //     const timeoutId = setTimeout(() => controller.abort(), 15000);
      
  //     const response = await fetch(`${fileUrl}?cb=${Date.now()}`, {
  //       cache: "no-store",
  //       mode: "cors",
  //       signal: controller.signal
  //     });

  //     if (!response.ok) throw new Error("Network error");

  //     const reader = response.body.getReader();
  //     const startTime = performance.now();
  //     let receivedLength = 0;
  //     let lastReportTime = startTime;
  //     let lastReportedBytes = 0;
  //     let isStreaming = true;

  //     const updateSpeedUI = (bytesDownloaded, timePassedSec) => {
  //       if (timePassedSec <= 0.005 || bytesDownloaded === 0) return;
        
  //       const bytesPerSecond = bytesDownloaded / timePassedSec;
  //       const bitsPerSecond = bytesPerSecond * 8;
  //       const kbPerSecond = bytesPerSecond / 1024;
  //       const speedMbps = (bitsPerSecond / (1024 * 1024)).toFixed(2);

  //       let formattedByteSpeed = "";
  //       if (bytesPerSecond >= 1024 * 1024) {
  //         formattedByteSpeed = (bytesPerSecond / (1024 * 1024)).toFixed(2) + " MB/s";
  //       } else {
  //         formattedByteSpeed = kbPerSecond.toFixed(2) + " KB/s";
  //       }

  //       setNetworkSpeed({ mbps: speedMbps, byteSpeedText: formattedByteSpeed, kbps: kbPerSecond });
  //     };

  //     while (isStreaming) {
  //       const { done, value } = await reader.read();
  //       if (value) receivedLength += value.length;

  //       const currentTime = performance.now();
  //       const timeDiff = (currentTime - lastReportTime) / 1000;

  //       if (done) {
  //         const totalTime = (currentTime - startTime) / 1000;
  //         updateSpeedUI(receivedLength, totalTime);
  //         isStreaming = false;
  //         break;
  //       }

  //       if (timeDiff >= 1) {
  //         const bytesSinceLast = receivedLength - lastReportedBytes;
  //         updateSpeedUI(bytesSinceLast, timeDiff);
  //         lastReportTime = currentTime;
  //         lastReportedBytes = receivedLength;
  //       }
  //     }
  //     clearTimeout(timeoutId);
  //   } catch (error) {
  //     if (error.name !== 'AbortError') {
  //       setNetworkSpeed({ mbps: 0, byteSpeedText: "0 KB/s", kbps: 0 });
  //     }
  //   }
  // }, [isDark]);

  // let currentSpeedColor = isDark ? "#a18300" : "#ffd000";
  // if (!navigator.onLine || networkSpeed.kbps === 0) {
  //   currentSpeedColor = isDark ? "#a60000" : "#ff0000";
  // } else if (networkSpeed.kbps !== null) {
  //   if (networkSpeed.kbps < 100) {
  //     currentSpeedColor = isDark ? "#a60000" : "#ff0000";
  //   } else if (networkSpeed.kbps > 400) {
  //     currentSpeedColor = isDark ? "#186718" : "#35e034";
  //   }
  // }

  // useEffect(() => {
  //   let isRunning = true;
  //   let timerId;

  //   const runContinuousSpeedTest = async () => {
  //     if (!isRunning) return;

  //     await measureLiveSpeed();

  //     if (isRunning) {
  //       timerId = setTimeout(runContinuousSpeedTest, 1000);
  //     }
  //   };

  //   runContinuousSpeedTest();

  //   const handleOffline = () => setNetworkSpeed({ mbps: 0, byteSpeedText: "0 KB/s", color: "#f45b31" });
  //   const handleOnline = () => runContinuousSpeedTest();

  //   window.addEventListener('offline', handleOffline);
  //   window.addEventListener('online', handleOnline);

  //   return () => {
  //     isRunning = false;
  //     clearTimeout(timerId);
  //     window.removeEventListener('offline', handleOffline);
  //     window.removeEventListener('online', handleOnline);
  //   };
  // }, [measureLiveSpeed]);

  return (
    <>
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? open : true}
        ModalProps={{
          keepMounted: true,
          onBackdropClick: toggleSidebar,
        }}
        onClose={toggleSidebar}
        className={`violet-sidebar ${open ? "open" : "closed"}`}
        PaperProps={{
          className: `violet-sidebar voilet-scroll-container ${open ? "open" : "closed"}`,
          sx: {
            backgroundColor: customColors && customColors.sidebar || theme.palette.background.sidebarbg,
            color: normalMenuTextColor,
            position: isMobile ? "absolute" : "fixed",
            top: isMobile ? 0 : 0,
            left: 0,
            height: "100%",
            width: open ? 250 : 60,
            transform: isMobile && !open ? "translateX(-100%)" : "translateX(0)",
            transition: "transform 0.3s ease-in-out, width 0.3s ease-in-out",
            zIndex: theme.zIndex.drawer + 1,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box className="sidebar-header"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignContent: 'center',
            gap: 1
          }}
        >

          {/* <Typography
            variant="body2"
            fontSize= '13px'
            color="text.secondary"
            color= "inherit"
            color= {currentSpeedColor}
          >
            {networkSpeed.mbps !== null
              ? networkSpeed.byteSpeedText
              : "Measuring..."}
          </Typography> */}

          <Box className="brand-container">
            {open ? (
              <Box
                component="img"
                src="/assets/images/logo/brandlogo.png"
                alt="VioletSync Logo"
                loading="eager"
                sx={{
                  width: { xs: 150 },
                  height: "auto",
                  display: "block",
                  borderRadius: 1,
                  objectFit: "cover",
                }}
              />
            ) : (
              <Box
                component="img"
                src="/assets/images/logo/mainlogo.png"
                alt="VioletSync Icon"
                loading="eager"
                sx={{
                  width: 30,
                  height: 30,
                  display: "block",
                  borderRadius: 1,
                  objectFit: "cover",
                }}
              />
            )}
          </Box>
        </Box>
        
        <Box
          sx={{ margin: "0 16px", marginBottom: "10px" }}
          onClick={() => {
            if (!open) toggleSidebar();
          }}
        >
          {open && (
            <CustomeSearchInput
              placeholder="Search menu..."
              onSearch={handleSearch}
              backgroundColor={isSidebarDark ? '#000' : undefined}
              textColor={isSidebarDark ? '#fff' : undefined}
              iconColor={isSidebarDark ? '#ffffff' : undefined}
              borderColor={isSidebarDark ? '#262d30' : (isSidebarWhite ? '#e9edf4' : undefined)}
              showIcon={true}
            />
          )}
        </Box>

        <Box sx={{ flexGrow: 1 }}>
        {sidebarRoutes?.length > 0 && (
  filteredRoutes.length > 0 ? (
    filteredRoutes.map((section, index) => {
      const sortedChildren = section.children?.slice().sort((a, b) => a.sortOrder - b.sortOrder);
      const IconComponent = getMenuIcon(section.menuIcon);
      const isAnyChildActive = section?.children?.some(
        (child) => child.routePath === location.pathname
      );
      const isParentActive = section.routePath === location.pathname || isAnyChildActive;

      return (
        <Box key={section.subMenuId || index} className="sidebar-section">
          <Box>
            <List className="menu-list">
              <ListItem className="menu-item">
                <ListItemButton
                  className="menu-button"
                  onClick={(e) => handleMenu(section, e)}
                  sx={{
                    backgroundColor: isParentActive
                      ? (customColors?.activeButton || theme.palette.background.bgactivemenu)
                      : "transparent",
                    color: isParentActive
                      ? activeMenuTextColor
                      : normalMenuTextColor,
                    borderRadius: 1,
                    "&:hover": {
                      backgroundColor: customColors?.activeButton || theme.palette.background.bgactivemenu,
                      color: activeMenuTextColor,
                    },
                  }}
                >
                  <Box className="menu-content">
                    <Box
                      className="menu-left"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: open ? "flex-start" : "center",
                        width: "100%",
                      }}
                    >
                      <i
                        className={`${section.menuIcon || "fa-sharp fa-regular fa-circle"} menu-icon`}
                        style={{ color: "inherit" }}
                      ></i>

                      {open && (
                        <Box
                          component="span"
                          className="menu-text"
                          style={{ color: "inherit", whiteSpace: "nowrap" }}
                        >
                          {section.subMenuName}
                          {section.tag && <SidebarStatusTag type={section.tag} />}
                        </Box>
                      )}
                    </Box>

                    {open && section?.children?.length > 0 && (
                      openMenus[section.subMenuId] ? (
                        <i className="fa-sharp fa-light fa-angle-down fs-10" style={{ color: "inherit" }}></i>
                      ) : (
                        <i className="fa-sharp fa-light fa-angle-right fs-10" style={{ color: "inherit" }}></i>
                      )
                    )}
                  </Box>
                </ListItemButton>

                {section?.children?.length > 0 && open && (
                  <Collapse in={openMenus[section.subMenuId]} timeout="auto" unmountOnExit>
                    {renderSubMenu(sortedChildren)}
                  </Collapse>
                )}
              </ListItem>
            </List>
          </Box>
        </Box>
      );
    })
  ) : (
    <Box
      sx={{
        p: 2,
        textAlign: "center",
        color: normalMenuTextColor,
        fontSize: "14px",
      }}
    >
      No matching menu items found
    </Box>
  )
)}
        </Box>
      </Drawer>

      <Popover
        open={Boolean(anchorEl) && !!activeFloatingMenu}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setActiveFloatingMenu(null);
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          elevation: 1,
          style: {
            minWidth: 200,
            marginLeft: 17,
            backgroundColor: sidebarColorRaw,
            color: normalMenuTextColor,
          },
        }}
      >
        {activeFloatingMenu?.children
          ?.filter((child) => child.isMenuShow)
          .map((child, index) => (
            <ListItemButton
              key={index}
              onClick={() => {
                navigate(child.routePath);
                setAnchorEl(null);
                setActiveFloatingMenu(null);
              }}
              sx={{
                fontSize: 13,
                color: location.pathname === child.routePath
                  ? activeMenuTextColor
                  : normalMenuTextColor,
                "&:hover": {
                  backgroundColor: customColors?.activeButton || theme.palette.custom.colorActive,
                  color: activeMenuTextColor,
                },
              }}
            >
              {child.subMenuName}
            </ListItemButton>
          ))}
      </Popover>
    </>
  );
};

export default Sidebar;